import type { ReactNode } from 'react';

import {
  FormFooter,
  FormHeader,
  FormHeaderFooterProvider,
  Surface,
} from '@storebrand-digital/pretzel';

import { CenteredLoadingIndicator } from '../../CenteredLoadingIndicator';
import { getInverseBackground } from '../getInverseBackground';
import { HeaderFooterGrid } from '../HeaderFooterGrid';
import {
  type NavigateToPathname,
  useResolveExitConfig,
} from './resolveExitConfig';
import type { FormHeaderFooterExit } from './types';
import type { HeaderFooterTheme } from '../types';
import { WebViewLayout } from '../WebViewLayout';

interface Props {
  children: ReactNode;
  exit?: FormHeaderFooterExit;
  theme: HeaderFooterTheme;
  isProd: boolean;
  isWebView: boolean;
  locale: 'no' | 'en';
  navigate: NavigateToPathname;
  onError: (error: unknown) => void;
  defaultExitPath?: string;
}

export function FormVariant({
  children,
  exit,
  theme,
  isProd,
  isWebView,
  locale,
  navigate,
  onError,
  defaultExitPath,
}: Props) {
  if (isWebView) {
    return (
      <WebViewLayout locale={locale} theme={theme}>
        {children}
      </WebViewLayout>
    );
  }

  return (
    <StandardLayout
      defaultExitPath={defaultExitPath}
      exit={exit}
      isProd={isProd}
      locale={locale}
      navigate={navigate}
      onError={onError}
      theme={theme}
    >
      {children}
    </StandardLayout>
  );
}

interface StandardLayoutProps {
  children: ReactNode;
  defaultExitPath?: string;
  exit?: FormHeaderFooterExit;
  isProd: boolean;
  locale: 'no' | 'en';
  navigate: NavigateToPathname;
  onError: (error: unknown) => void;
  theme: HeaderFooterTheme;
}

function StandardLayout({
  children,
  defaultExitPath,
  exit,
  isProd,
  locale,
  navigate,
  onError,
  theme,
}: StandardLayoutProps) {
  const resolvedExit = useResolveExitConfig({
    defaultExitPath,
    exit: exit ?? {
      label: locale === 'en' ? 'Cancel' : 'Avbryt',
      type: 'default',
    },
    isProd,
    navigate,
  });

  return (
    <FormHeaderFooterProvider
      environment={isProd ? 'production' : 'test'}
      exitButtonLabel={resolvedExit?.label}
      exitEvent={!!resolvedExit}
      exitUrl={resolvedExit?.url}
      hideCart
      hideQuestionButton
      loadingComponent={<CenteredLoadingIndicator isFixed delayMs={1_000} />}
      onError={onError}
      onExitEvent={resolvedExit?.onExitEvent}
    >
      <HeaderFooterGrid>
        <Surface isBackground lightThemeBackgroundLayer={theme}>
          <FormHeader />
        </Surface>

        <Surface
          isBackground
          lightThemeBackgroundLayer={theme}
          className="flex flex-col box-border"
        >
          {children}
        </Surface>

        <Surface
          isBackground
          lightThemeBackgroundLayer={getInverseBackground(theme)}
        >
          <FormFooter />
        </Surface>
      </HeaderFooterGrid>
    </FormHeaderFooterProvider>
  );
}
